import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Users, User, PieChart, Bell, Target, Repeat,
  TrendingUp, Wallet, Globe, Lock, Fingerprint, Download, ShieldCheck,
  Heart, Sparkles, ChevronLeft, ChevronRight,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";

/* ─── App screenshots (CSS-rendered mockup screens) ─── */
const SCREENS = ["splash", "dashboard", "categories", "goals"];

const FEATURES = [
  { icon: Users, name: "Couple accounts", desc: "Share a joint space with your partner. Track shared expenses, incomes and goals side-by-side." },
  { icon: User, name: "Personal tracker", desc: "A clean personal ledger for your own income, spends and savings — no partner required." },
  { icon: PieChart, name: "Smart categories & budgets", desc: "Auto-categorised transactions, monthly budgets per category, and instant alerts." },
  { icon: Bell, name: "Bill reminders", desc: "Never miss rent, EMI, credit-card or utility due dates. Gentle reminders ahead of time." },
  { icon: Repeat, name: "Recurring expenses", desc: "Set once, tracked forever. Subscriptions and standing instructions flow in automatically." },
  { icon: Target, name: "Shared goals", desc: "House down-payment, dream vacation, emergency fund — set a target and watch the progress fill." },
  { icon: TrendingUp, name: "Reports & insights", desc: "Where your money actually goes, month over month. Clean charts, plain-English insights." },
  { icon: Wallet, name: "Multi-account", desc: "Bank, wallet, cash, credit cards, UPI — one dashboard, one net-worth number." },
  { icon: Globe, name: "Multi-currency", desc: "Travel or earn abroad? Log expenses in any currency, converted to your base at live rates." },
  { icon: Fingerprint, name: "Biometric lock", desc: "Face ID / fingerprint on app launch. Your finances stay yours even if the phone is unlocked." },
  { icon: Lock, name: "End-to-end encryption", desc: "Sensitive fields are encrypted on-device before they ever leave your phone." },
  { icon: Download, name: "Export & own your data", desc: "One-tap CSV/JSON export. It's your money — and your data." },
];

const PRIVACY = [
  { h: "1. Information we collect", p: "We collect only the data necessary to provide our services. Phone number — used for OTP-based authentication. Financial data — expenses, income, budgets and goals entered by you. User content — notes and transaction descriptions. App activity — app usage interactions for improving functionality. Device information — basic device identifiers for security and performance." },
  { h: "2. How we use your data", p: "To provide the core app functionality (expense tracking, budgeting, goals). To improve app performance and user experience. To enable secure login and account management." },
  { h: "3. Data storage & security", p: "Your data is securely stored using Supabase. We implement encryption in transit, secure access controls, and industry-standard security practices." },
  { h: "4. Data sharing", p: "Your data is not sold or shared with advertisers. Data is only shared between linked household partners if you enable this. We do not share your data with any third parties except essential infrastructure services required to run the app." },
  { h: "5. No financial access", p: "FINUS does not connect to bank accounts, UPI, or financial institutions. All financial data is manually entered by users." },
  { h: "6. User control & data deletion", p: "You have full control over your data. You can delete your account any time from Settings → Delete Account. Account deletion permanently removes all associated data. You may also contact us for support." },
  { h: "7. Data retention", p: "We retain your data only while your account is active. Once deleted, your data is permanently removed from our systems." },
  { h: "8. Children's privacy", p: "FINUS is not intended for users under 18 years of age. We do not knowingly collect data from anyone under 18." },
  { h: "9. Your rights (India DPDP + GDPR aligned)", p: "You have the right to access your data, correct your data, delete your data, and withdraw your consent at any time. To exercise any of these rights, contact us at hello@yugiq.in." },
  { h: "10. Contact us", p: "For any privacy questions or requests, email hello@yugiq.in." },
];

/* ─── Store Badges ─── */
function PlayStoreBadge() {
  return (
    <a
      href="https://play.google.com/store"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="finus-playstore-btn"
      className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-5 py-3 hover:bg-gray-900 transition-colors"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M3.18 23.94c.36.2.77.24 1.16.12l11.64-11.64L12.52 9l-9.34 14.94zm-.92-20.8C2.1 3.4 2 3.7 2 4.06v15.88c0 .36.1.66.26.92L14.78 8.4 2.26 3.14zM20.54 10.2l-2.7-1.56-3.2 3.2 3.2 3.2 2.72-1.57c.78-.45.78-1.82-.02-2.27zM4.34.06c-.4-.12-.8-.08-1.16.12L15.9 12.9l3.46-3.46L4.34.06z" />
      </svg>
      <div className="text-left leading-tight">
        <div className="text-[10px] opacity-70">Get it on</div>
        <div className="text-[14px] font-semibold">Google Play</div>
      </div>
    </a>
  );
}

function AppStoreBadge() {
  return (
    <a
      href="https://apps.apple.com"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="finus-appstore-btn"
      className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-5 py-3 hover:bg-gray-900 transition-colors"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
      <div className="text-left leading-tight">
        <div className="text-[10px] opacity-70">Download on the</div>
        <div className="text-[14px] font-semibold">App Store</div>
      </div>
    </a>
  );
}

/* ─── CSS App Screens ─── */
function SplashScreen() {
  return (
    <div className="w-full h-full bg-[#0A0A12] flex flex-col items-center justify-between py-10 px-6">
      <div />
      <div className="flex flex-col items-center gap-3">
        <h1 className="text-[52px] font-black text-[#6C3AED] tracking-[-2px] leading-none">FINUS</h1>
        <div className="w-12 h-[2px] bg-[#6C3AED] rounded-full" />
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[#6C3AED] text-[13px] font-semibold">Shop</span>
          <span className="text-white/30 text-[10px]">·</span>
          <span className="text-white/60 text-[13px]">Track</span>
          <span className="text-white/30 text-[10px]">·</span>
          <span className="text-[#6C3AED] text-[13px] font-semibold">Grow</span>
          <span className="text-white/30 text-[10px]">·</span>
          <span className="text-white/60 text-[13px]">Repeat</span>
        </div>
      </div>
      <div className="w-full flex flex-col items-center gap-3">
        <div className="w-full bg-[#6C3AED] rounded-2xl py-3.5 text-white text-center text-[14px] font-semibold">Start with your number</div>
        <p className="text-[9px] tracking-[0.15em] uppercase text-white/25">A YUGIQ TECHNOLOGIES product</p>
      </div>
    </div>
  );
}

function DashboardScreen() {
  return (
    <div className="w-full h-full bg-[#0A0A12] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/5">
        <div className="text-white/60 text-[16px]">☰</div>
        <span className="text-[#6C3AED] font-black text-[15px] tracking-[-0.5px]">FINUS</span>
        <div className="w-7 h-7 rounded-full bg-[#6C3AED] flex items-center justify-center text-white text-[11px] font-bold">S</div>
      </div>
      <div className="flex-1 overflow-hidden px-3 py-2 space-y-2.5">
        <p className="text-white text-[11px] font-medium">Good afternoon, Sagar</p>
        <div className="flex items-center justify-center gap-2 text-[10px] text-white/40">
          <span>‹</span><span>This month</span><span>›</span>
        </div>
        {/* Expense card */}
        <div className="rounded-2xl bg-[#1A1035] p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-bold tracking-[0.12em] text-white/40 uppercase">Expenses</span>
            <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] text-white/40">👤</div>
          </div>
          <div className="text-white font-black text-[26px] tracking-tight leading-none">₹97,407</div>
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-[#D4890A]" style={{ width: "87%" }} />
          </div>
          <div className="mt-1 text-[9px] text-[#4ECDC4] font-medium">₹14,593 left · on track</div>
        </div>
        {/* Partner split */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-[#151520] p-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#4ECDC4] flex items-center justify-center text-[10px] font-bold text-[#0A0A12]">S</div>
            <div><div className="text-white text-[9px] font-semibold">Samiksha</div><div className="text-white/40 text-[8px]">₹30,148 (31%)</div></div>
          </div>
          <div className="rounded-xl bg-[#151520] p-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#6C3AED] flex items-center justify-center text-[10px] font-bold text-white">S</div>
            <div><div className="text-white text-[9px] font-semibold">Sagar</div><div className="text-white/40 text-[8px]">₹67,259 (69%)</div></div>
          </div>
        </div>
        {/* Categories header */}
        <div className="flex items-center justify-between">
          <span className="text-white text-[10px] font-semibold">Categories</span>
          <div className="bg-[#1A1035] border border-[#6C3AED]/40 rounded-full px-2 py-0.5 text-[8px] text-[#6C3AED]">≡ Set Budget</div>
        </div>
      </div>
      {/* Bottom nav */}
      <div className="flex items-end justify-around px-3 pb-3 pt-2 border-t border-white/5">
        <div className="flex flex-col items-center gap-0.5"><div className="text-[14px]">🏠</div><div className="text-[8px] text-[#6C3AED]">Home</div></div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">₹</div><div className="text-[8px] text-white/30">Expenses</div></div>
        <div className="w-9 h-9 rounded-full bg-[#6C3AED] flex items-center justify-center text-white text-[16px] -mt-4 shadow-lg">+</div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">💡</div><div className="text-[8px] text-white/30">Insights</div></div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">◎</div><div className="text-[8px] text-white/30">Goals</div></div>
      </div>
    </div>
  );
}

function CategoriesScreen() {
  return (
    <div className="w-full h-full bg-[#0A0A12] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/5">
        <div className="text-white/60 text-[16px]">☰</div>
        <span className="text-[#6C3AED] font-black text-[15px] tracking-[-0.5px]">FINUS</span>
        <div className="w-7 h-7 rounded-full bg-[#6C3AED] flex items-center justify-center text-white text-[11px] font-bold">S</div>
      </div>
      <div className="flex-1 overflow-hidden px-3 py-3 space-y-2.5">
        <div className="rounded-2xl bg-[#151520] p-3 space-y-2.5">
          {/* Toggle */}
          <div className="flex bg-[#0A0A12] rounded-full p-0.5 gap-0.5">
            <div className="flex-1 bg-[#6C3AED] rounded-full py-1 text-center text-[9px] text-white font-bold flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full border border-white/60" />Needs</div>
            <div className="flex-1 py-1 text-center text-[9px] text-white/40 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full border border-white/30" />Wants</div>
            <div className="flex-1 py-1 text-center text-[9px] text-white/40 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full border border-red-400/50" />Savings</div>
          </div>
          <div className="text-center text-[9px] text-white/30">₹70,235 this month</div>
          {/* Category rows */}
          {[
            { emoji: "🏦", name: "EMIs", spent: "₹59,903", budget: "₹60,000", pct: 99, color: "#D4890A", onTrack: true },
            { emoji: "⚡", name: "Utilities", spent: "₹5,986", budget: "₹5,000", pct: 100, color: "#EF4444", onTrack: false },
            { emoji: "🍽️", name: "Food", spent: "₹2,577", budget: "₹5,000", pct: 52, color: "#4ECDC4", onTrack: true },
            { emoji: "🛒", name: "Groceries", spent: "₹1,040", budget: "₹5,000", pct: 21, color: "#4ECDC4", onTrack: true },
          ].map((c) => (
            <div key={c.name} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[9px] text-white font-medium">{c.emoji} {c.name}</span>
                <span className="text-[8px] text-white/50">{c.spent} / {c.budget}</span>
              </div>
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${c.pct}%`, background: c.color }} />
              </div>
            </div>
          ))}
          <div className="text-center text-[9px] text-[#6C3AED] font-medium">3 more ∨</div>
        </div>
      </div>
      <div className="flex items-end justify-around px-3 pb-3 pt-2 border-t border-white/5">
        <div className="flex flex-col items-center gap-0.5"><div className="text-[14px]">🏠</div><div className="text-[8px] text-white/30">Home</div></div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">₹</div><div className="text-[8px] text-white/30">Expenses</div></div>
        <div className="w-9 h-9 rounded-full bg-[#6C3AED] flex items-center justify-center text-white text-[16px] -mt-4 shadow-lg">+</div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">💡</div><div className="text-[8px] text-white/30">Insights</div></div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">◎</div><div className="text-[8px] text-white/30">Goals</div></div>
      </div>
    </div>
  );
}

function GoalsScreen() {
  return (
    <div className="w-full h-full bg-[#0A0A12] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/5">
        <span className="text-[#6C3AED] font-black text-[15px] tracking-[-0.5px]">FINUS</span>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#6C3AED] flex items-center justify-center text-white text-[11px] font-bold">S</div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden px-3 py-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-white text-[16px] font-black">Goals</span>
          <div className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-white/60 text-[13px]">+</div>
        </div>
        {/* Goal cards */}
        <div className="rounded-2xl bg-[#0D1F1A] border border-[#4ECDC4]/20 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#4ECDC4]/20 flex items-center justify-center text-[14px]">✈️</div>
            <div className="flex-1"><div className="text-white text-[11px] font-bold">Goa trip</div><div className="text-[#4ECDC4] text-[8px] font-semibold uppercase tracking-wider">TRIP</div></div>
            <div className="text-[#4ECDC4] text-[9px] font-bold bg-[#4ECDC4]/15 rounded-full px-2 py-0.5">0.1%</div>
          </div>
          <div className="h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-[#4ECDC4]" style={{ width: "1%" }} /></div>
          <div className="flex justify-between text-[8px] text-white/40"><span>₹10 saved</span><span>Target ₹20,000</span></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-full bg-[#6C3AED] flex items-center justify-center text-[7px] text-white">S</div><span className="text-[8px] text-white/30">1 entry ∨</span></div>
            <div className="border border-[#4ECDC4]/50 rounded-full px-2 py-0.5 text-[8px] text-[#4ECDC4]">+ Add money</div>
          </div>
        </div>
        <div className="rounded-2xl bg-[#1F1400] border border-[#D4890A]/20 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#D4890A]/20 flex items-center justify-center text-[14px]">⭐</div>
            <div className="flex-1"><div className="text-white text-[11px] font-bold">School</div><div className="text-[#D4890A] text-[8px] font-semibold uppercase tracking-wider">KIDS</div></div>
            <div className="text-[#D4890A] text-[9px] font-bold bg-[#D4890A]/15 rounded-full px-2 py-0.5">0.3%</div>
          </div>
          <div className="h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-[#D4890A]" style={{ width: "1%" }} /></div>
          <div className="flex justify-between text-[8px] text-white/40"><span>₹500 saved</span><span>Target ₹2,00,000</span></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-full bg-[#6C3AED] flex items-center justify-center text-[7px] text-white">S</div><span className="text-[8px] text-white/30">1 entry ∨</span></div>
            <div className="border border-[#D4890A]/50 rounded-full px-2 py-0.5 text-[8px] text-[#D4890A]">+ Add money</div>
          </div>
        </div>
      </div>
      <div className="flex items-end justify-around px-3 pb-3 pt-2 border-t border-white/5">
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">🏠</div><div className="text-[8px] text-white/30">Home</div></div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">₹</div><div className="text-[8px] text-white/30">Expenses</div></div>
        <div className="w-9 h-9 rounded-full bg-[#6C3AED] flex items-center justify-center text-white text-[16px] -mt-4 shadow-lg">+</div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[12px] text-white/30">💡</div><div className="text-[8px] text-white/30">Insights</div></div>
        <div className="flex flex-col items-center gap-0.5"><div className="text-[14px]">◎</div><div className="text-[8px] text-[#6C3AED]">Goals</div></div>
      </div>
    </div>
  );
}

function PhoneMockup({ screen }) {
  const renderScreen = () => {
    if (screen === "splash") return <SplashScreen />;
    if (screen === "dashboard") return <DashboardScreen />;
    if (screen === "categories") return <CategoriesScreen />;
    return <GoalsScreen />;
  };
  return (
    <div className="relative mx-auto" style={{ width: 240, height: 490 }}>
      {/* Phone shell */}
      <div className="absolute inset-0 rounded-[40px] bg-[#1A1A2E] shadow-2xl" style={{ boxShadow: "0 40px 80px rgba(108,58,237,0.25), 0 0 0 8px #0F0F1A, 0 0 0 10px #2A2A3E" }} />
      {/* Screen area */}
      <div className="absolute inset-[10px] rounded-[32px] overflow-hidden bg-[#0A0A12]">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 w-16 h-5 bg-[#1A1A2E] rounded-b-2xl flex items-center justify-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#0A0A12]/60" />
          <div className="w-2 h-2 rounded-full bg-[#0A0A12]/60" />
        </div>
        {/* Status bar */}
        <div className="relative z-10 flex items-center justify-between px-5 pt-1 pb-0">
          <span className="text-white text-[8px] font-semibold">9:41</span>
          <div className="flex items-center gap-0.5">
            <span className="text-white text-[7px]">▌▌▌</span>
            <span className="text-white text-[7px]">◈</span>
            <span className="text-white text-[7px]">▐</span>
          </div>
        </div>
        {/* App content */}
        <div className="absolute inset-0 top-[22px]">
          {renderScreen()}
        </div>
      </div>
      {/* Side buttons */}
      <div className="absolute left-[-3px] top-20 w-1 h-8 bg-[#2A2A3E] rounded-l-md" />
      <div className="absolute left-[-3px] top-32 w-1 h-12 bg-[#2A2A3E] rounded-l-md" />
      <div className="absolute left-[-3px] top-48 w-1 h-12 bg-[#2A2A3E] rounded-l-md" />
      <div className="absolute right-[-3px] top-24 w-1 h-16 bg-[#2A2A3E] rounded-r-md" />
    </div>
  );
}

/* ─── Stat badges ─── */
const STATS = [
  { value: "100%", label: "Private" },
  { value: "₹0", label: "Forever free" },
  { value: "iOS + Android", label: "Both platforms" },
];

const SCREEN_LABELS = ["Splash", "Dashboard", "Categories", "Goals"];

export default function Finus() {
  const [activeScreen, setActiveScreen] = useState(0);

  useSeo({
    title: "FINUS — Personal & Couple Finance Tracker by Yugiq",
    description: "FINUS is a private, beautifully simple personal and couple finance tracker. Split expenses, track budgets, hit shared goals — with bank-grade encryption.",
    canonical: "https://yugiq.in/finus",
    keywords: "FINUS app, couple finance tracker, personal finance app India, expense tracker for couples, budget app, shared expenses app",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "FINUS",
      applicationCategory: "FinanceApplication",
      operatingSystem: "iOS, Android",
      description: "Personal and couple finance tracker with shared budgets, goals and privacy-first design.",
      publisher: { "@type": "Organization", name: "Yugiq Technologies" },
      offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
    },
  });

  // Auto-advance screens
  useEffect(() => {
    const id = setInterval(() => setActiveScreen((p) => (p + 1) % SCREENS.length), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <main className="min-h-screen bg-white text-[#0F0F1A]" data-testid="finus-page">

      {/* ── Sticky Nav ── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="finus-logo">
            <span className="w-8 h-8 rounded-[9px] overflow-hidden">
              <img src="/logo.png" alt="Yugiq" className="w-full h-full object-cover" />
            </span>
            <span className="font-extrabold text-[15px] tracking-tight text-[#0F0F1A]">Yugiq</span>
          </Link>
          <div className="flex items-center gap-2">
            <a href="#privacy" className="hidden sm:inline-flex items-center text-[13px] text-gray-500 hover:text-[#6C3AED] transition-colors font-medium" data-testid="finus-nav-privacy">Privacy Policy</a>
            <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6C3AED] border border-[#6C3AED]/30 rounded-full px-4 py-1.5 hover:bg-[#6C3AED]/5 transition-colors" data-testid="finus-back-home">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden pt-16 pb-10 sm:pt-24 sm:pb-20" data-testid="finus-hero">
        {/* Background accent */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle, #6C3AED 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-5 pointer-events-none" style={{ background: "radial-gradient(circle, #6C3AED 0%, transparent 70%)", transform: "translate(-40%, 40%)" }} />

        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left copy */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#6C3AED]/8 border border-[#6C3AED]/20 rounded-full px-3.5 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6C3AED] animate-pulse" />
                <span className="text-[12px] font-semibold text-[#6C3AED] tracking-wide uppercase">A Yugiq Product</span>
              </div>

              <h1 className="text-[58px] sm:text-[72px] font-black leading-none tracking-[-3px] text-[#0F0F1A]" data-testid="finus-headline">
                FINUS
              </h1>
              <p className="text-[22px] sm:text-[26px] font-light text-gray-400 tracking-tight mt-1 mb-5">
                Money, <span className="text-[#6C3AED] font-semibold">together.</span>
              </p>

              <p className="text-[16px] sm:text-[17px] leading-relaxed text-gray-500 max-w-md mb-8" data-testid="finus-sub">
                A beautifully simple <span className="text-[#0F0F1A] font-semibold">personal &amp; couple</span> finance tracker. Split expenses, track budgets, hit shared goals — with bank-grade encryption and zero ads.
              </p>

              {/* Store buttons */}
              <div className="flex flex-wrap gap-3 mb-8" data-testid="finus-store-buttons">
                <PlayStoreBadge />
                <AppStoreBadge />
              </div>

              {/* Trust pills */}
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: ShieldCheck, text: "Encrypted" },
                  { icon: Heart, text: "Made for couples" },
                  { icon: Lock, text: "No bank access" },
                ].map(({ icon: Icon, text }) => (
                  <span key={text} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-gray-500 bg-gray-50 border border-gray-100 rounded-full px-3 py-1">
                    <Icon className="w-3.5 h-3.5 text-[#6C3AED]" /> {text}
                  </span>
                ))}
              </div>
            </div>

            {/* Right phone mockup */}
            <div className="flex flex-col items-center gap-6" data-testid="finus-preview">
              <PhoneMockup screen={SCREENS[activeScreen]} />

              {/* Screen selector */}
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveScreen((p) => (p - 1 + SCREENS.length) % SCREENS.length)} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:border-[#6C3AED] hover:text-[#6C3AED] transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {SCREENS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveScreen(i)}
                    className={`text-[11px] font-semibold px-3 py-1 rounded-full transition-all ${activeScreen === i ? "bg-[#6C3AED] text-white" : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}
                  >
                    {SCREEN_LABELS[i]}
                  </button>
                ))}
                <button onClick={() => setActiveScreen((p) => (p + 1) % SCREENS.length)} className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:border-[#6C3AED] hover:text-[#6C3AED] transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section className="border-y border-gray-100 bg-gray-50/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-3 gap-4 text-center">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="text-[22px] sm:text-[28px] font-black text-[#6C3AED] tracking-tight">{s.value}</div>
              <div className="text-[12px] text-gray-400 font-medium mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 sm:py-28" data-testid="finus-features">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-xl mb-12">
            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#6C3AED] mb-3">// What FINUS does</div>
            <h2 className="text-[32px] sm:text-[42px] font-black tracking-tight text-[#0F0F1A] leading-tight">
              Everything you need.{" "}
              <span className="text-gray-300">Nothing you don't.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.name}
                  className="group p-5 rounded-2xl border border-gray-100 hover:border-[#6C3AED]/30 hover:shadow-lg hover:shadow-[#6C3AED]/5 transition-all bg-white"
                  data-testid={`finus-feature-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-[#6C3AED]/8 group-hover:bg-[#6C3AED]/12 transition-colors">
                    <Icon className="w-5 h-5 text-[#6C3AED]" />
                  </div>
                  <div className="font-bold text-[14px] text-[#0F0F1A] mb-1.5">{f.name}</div>
                  <p className="text-[13px] leading-relaxed text-gray-400">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Security trio ── */}
      <section className="border-y border-gray-100 bg-[#0F0F1A] py-14 sm:py-18">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-8">
          {[
            { icon: Lock, h: "End-to-end encryption", p: "TLS 1.2+ in transit. Sensitive fields encrypted on-device before they leave your phone." },
            { icon: Fingerprint, h: "Biometric lock", p: "Face ID / fingerprint required to open the app — even if your phone is unlocked." },
            { icon: ShieldCheck, h: "No banks connected", p: "FINUS never asks for your net-banking credentials. You control what gets tracked." },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.h} className="flex gap-4">
                <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-[#6C3AED]/15 border border-[#6C3AED]/25">
                  <Icon className="w-5 h-5 text-[#9B72F5]" />
                </div>
                <div>
                  <div className="text-white font-bold text-[15px]">{s.h}</div>
                  <p className="mt-1 text-[13px] leading-relaxed text-white/40">{s.p}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Download CTA ── */}
      <section className="py-20 sm:py-28" data-testid="finus-download-section">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#6C3AED] mb-3">// Download</div>
          <h2 className="text-[32px] sm:text-[42px] font-black tracking-tight text-[#0F0F1A] leading-tight mb-4">
            Available now on both platforms
          </h2>
          <p className="text-[16px] text-gray-400 mb-8">
            Free to download. No hidden charges. No ads. Just clean, private finance tracking.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <PlayStoreBadge />
            <AppStoreBadge />
          </div>
        </div>
      </section>

      {/* ── Privacy Policy ── */}
      <section id="privacy" className="py-20 sm:py-28 border-t border-gray-100 bg-gray-50/40" data-testid="finus-privacy">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#6C3AED] mb-3">// Privacy policy</div>
          <h2 className="text-[32px] sm:text-[42px] font-black tracking-tight text-[#0F0F1A] leading-tight mb-2">
            Your money. Your data.{" "}
            <span className="text-[#6C3AED]">Never for sale.</span>
          </h2>
          <p className="text-[13px] font-semibold tracking-widest uppercase text-gray-300 mb-10">
            Effective 01 Jan 2026 · Yugiq Technologies
          </p>

          <div className="space-y-8">
            {PRIVACY.map((sec, i) => (
              <div key={sec.h} data-testid={`finus-privacy-${i}`}>
                <h3 className="font-extrabold text-[17px] text-[#0F0F1A] mb-2">{sec.h}</h3>
                <p className="text-[14.5px] leading-[1.75] text-gray-500">{sec.p}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white border border-gray-100 rounded-2xl p-6 flex items-start gap-4 shadow-sm">
            <Sparkles className="w-5 h-5 text-[#6C3AED] mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-[15px] text-[#0F0F1A]">Questions about privacy?</div>
              <p className="mt-1 text-[14px] text-gray-500">Email <a href="mailto:hello@yugiq.in" className="text-[#6C3AED] hover:underline font-medium">hello@yugiq.in</a> and we'll respond within 30 days.</p>
              <Link to="/finus/delete-account" className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-red-500 hover:underline" data-testid="finus-delete-account-link">
                Request account deletion →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[11px] tracking-[0.2em] uppercase text-gray-300 font-semibold">
            © {new Date().getFullYear()} FINUS · A Yugiq product
          </div>
          <Link to="/" className="text-[12px] font-semibold text-[#6C3AED] hover:underline">
            yugiq.in →
          </Link>
        </div>
      </footer>
    </main>
  );
}
